'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { CrewMember } from './CalendarInteractive';

interface CrewSidebarProps {
  crew: CrewMember[];
}

const availabilityConfig = {
  available: { label: 'Available', color: 'bg-success', text: 'text-success' },
  assigned: { label: 'On Job', color: 'bg-primary', text: 'text-primary' },
  off: { label: 'Off Today', color: 'bg-muted-foreground', text: 'text-muted-foreground' },
};

export default function CrewSidebar({ crew }: CrewSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = crew.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.role.toLowerCase().includes(search.toLowerCase())
  );

  const available = crew.filter(c => c.availability === 'available').length;
  const assigned = crew.filter(c => c.availability === 'assigned').length;

  if (collapsed) {
    return (
      <div className="hidden lg:flex w-10 bg-card border-r border-border flex-col items-center py-3 gap-3 flex-shrink-0">
        <button onClick={() => setCollapsed(false)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-standard" aria-label="Expand crew sidebar" suppressHydrationWarning>
          <Icon name="ChevronRightIcon" size={16} />
        </button>
        <div className="w-6 h-6 rounded-full bg-success flex items-center justify-center">
          <span className="text-white text-xs font-heading font-bold">{available}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="hidden lg:flex w-[280px] xl:w-[300px] bg-card border-r border-border flex-col flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <h3 className="font-heading font-semibold text-foreground text-sm">Crew Roster</h3>
          <p className="font-caption text-xs text-muted-foreground mt-0.5">
            <span className="text-success font-medium">{available} available</span>
            {' · '}
            <span className="text-primary font-medium">{assigned} on job</span>
          </p>
        </div>
        <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-standard" aria-label="Collapse crew sidebar" suppressHydrationWarning>
          <Icon name="ChevronLeftIcon" size={16} />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-border">
        <div className="relative">
          <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search crew..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm font-body bg-muted border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:bg-card text-foreground placeholder:text-muted-foreground"
            suppressHydrationWarning
          />
        </div>
      </div>

      {/* Crew List */}
      <div className="flex-1 overflow-y-auto py-2">
        {filtered.map(member => {
          const avail = availabilityConfig[member.availability];
          return (
            <div key={member.id} className="px-3 py-2.5 hover:bg-muted transition-standard cursor-pointer group">
              <div className="flex items-start gap-2.5">
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 rounded-full overflow-hidden">
                    <AppImage
                      src={member.avatar}
                      alt={`Profile photo of ${member.name}, ${member.role}`}
                      width={36}
                      height={36}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${avail.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <p className="font-body text-sm font-medium text-foreground truncate">{member.name}</p>
                    <span className={`font-caption text-[10px] ${avail.text} flex-shrink-0`}>{avail.label}</span>
                  </div>
                  <p className="font-caption text-xs text-muted-foreground">{member.role}</p>
                  {member.currentJob && (
                    <p className="font-caption text-xs text-primary truncate mt-0.5">{member.currentJob}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {member.skills.slice(0, 3).map(skill => (
                      <span key={skill} className="font-caption text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-sm">
                        {skill}
                      </span>
                    ))}
                    {member.skills.length > 3 && (
                      <span className="font-caption text-[10px] text-muted-foreground">+{member.skills.length - 3}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-standard">
                <button suppressHydrationWarning className="flex items-center gap-1 px-2 py-1 text-xs font-caption bg-primary/10 text-primary rounded-sm hover:bg-primary/20 transition-standard">
                  <Icon name="PhoneIcon" size={11} />
                  {member.phone}
                </button>
                {member.availability === 'available' && (
                  <button suppressHydrationWarning className="flex items-center gap-1 px-2 py-1 text-xs font-caption bg-success/10 text-success rounded-sm hover:bg-success/20 transition-standard">
                    <Icon name="PlusIcon" size={11} />
                    Assign
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Smart Suggest */}
      <div className="px-3 py-3 border-t border-border bg-muted/50">
        <button suppressHydrationWarning className="w-full flex items-center justify-center gap-2 py-2 text-sm font-body font-medium text-secondary bg-secondary/10 hover:bg-secondary/20 rounded-md transition-standard">
          <Icon name="SparklesIcon" size={15} variant="solid" />
          Smart Schedule Suggest
        </button>
      </div>
    </div>
  );
}